/**
 * Vercel AI SDK adapter for Explore subagent
 */

import { tool, generateText, stepCountIs } from 'ai';
import type { LanguageModel } from 'ai';
import { z } from 'zod';
import { WarpGrepClient } from '../tools/warp_grep/client.js';
import { formatResult } from '../tools/warp_grep/client.js';
import type { WarpGrepContext } from '../tools/warp_grep/types.js';
import { getSystemPrompt, CODEBASE_SEARCH_TOOL, SEND_MESSAGE_TOOL } from './prompts.js';
import type {
  ExploreSubagentConfig,
  ExploreResult,
  ExploreStep,
  ExploreSession,
  ExploreEvent,
  SubagentMessage,
  MessageHandler,
  StepHandler,
} from './types.js';

/** Config for Vercel AI SDK Explore subagent */
export interface VercelExploreConfig extends ExploreSubagentConfig {
  /** Vercel AI SDK model instance */
  model: LanguageModel;
}

const DEFAULT_MAX_TURNS: Record<string, number> = {
  quick: 3,
  medium: 6,
  thorough: 12,
};

function getMaxTurns(config: ExploreSubagentConfig): number {
  return config.maxTurns ?? DEFAULT_MAX_TURNS[config.thoroughness ?? 'medium'] ?? 6;
}

/**
 * Create an Explore subagent using Vercel AI SDK
 */
export function createExploreSubagent(config: VercelExploreConfig) {
  if (!config.repoRoot) {
    throw new Error('repoRoot is required for Explore subagent');
  }

  const warpGrep = new WarpGrepClient({
    morphApiKey: config.morphApiKey,
  });

  const thoroughness = config.thoroughness ?? 'medium';
  const replyTimeout = config.replyTimeout ?? 30_000;

  /** Run the agent loop and collect results */
  async function runExplore(
    prompt: string,
    callbacks: {
      onStep?: StepHandler;
      onMessage?: MessageHandler;
    } = {},
  ): Promise<ExploreResult> {
    const startTime = Date.now();
    const allContexts: WarpGrepContext[] = [];
    let searchCount = 0;

    const timeoutMs = config.timeout;
    const timeoutPromise = timeoutMs
      ? new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(`Exploration timed out after ${timeoutMs}ms`)), timeoutMs),
        )
      : null;

    const runPromise = (async (): Promise<ExploreResult> => {
      try {
        const codebaseSearchSchema = z.object({
          searchRequest: z.string().describe('Natural language description of what to search for'),
        });

        const sendMessageSchema = z.object({
          message: z.string().describe('The message to send to the orchestrator'),
        });

        const result = await generateText({
          model: config.model,
          system: getSystemPrompt(thoroughness),
          prompt,
          tools: {
            codebase_search: tool({
              description: CODEBASE_SEARCH_TOOL.description,
              inputSchema: codebaseSearchSchema,
              execute: async (params: z.infer<typeof codebaseSearchSchema>) => {
                searchCount++;
                const warpResult = await warpGrep.execute({
                  searchTerm: params.searchRequest,
                  repoRoot: config.repoRoot,
                  excludes: config.excludes,
                  includes: config.includes,
                });

                if (warpResult.contexts) {
                  allContexts.push(...warpResult.contexts);
                }

                callbacks.onStep?.({
                  step: searchCount,
                  searchRequest: params.searchRequest,
                  contextsFound: warpResult.contexts?.length ?? 0,
                  isFinal: false,
                });

                return formatResult(warpResult);
              },
            }),
            send_message: tool({
              description: SEND_MESSAGE_TOOL.description,
              inputSchema: sendMessageSchema,
              execute: async (params: z.infer<typeof sendMessageSchema>) => {
                const subagentMsg: SubagentMessage = {
                  from: 'explore',
                  content: params.message,
                  timestamp: Date.now(),
                };

                // Create a promise that waits for the host's reply
                const replyPromise = new Promise<string>((resolve) => {
                  // Emit the message to the host via callback
                  if (callbacks.onMessage) {
                    callbacks.onMessage(subagentMsg, (replyText: string) => {
                      resolve(replyText);
                    });
                  } else {
                    // No handler registered, auto-resolve after timeout
                    setTimeout(() => resolve(''), replyTimeout);
                  }
                });

                // Wait for reply with timeout
                const reply = await Promise.race([
                  replyPromise,
                  new Promise<string>((resolve) =>
                    setTimeout(() => resolve(''), replyTimeout),
                  ),
                ]);

                if (reply) {
                  return `Response from orchestrator: ${reply}`;
                }
                return 'No response. Continue with your best judgment.';
              },
            }),
          },
          stopWhen: stepCountIs(getMaxTurns(config)),
        });

        // Emit final step
        callbacks.onStep?.({
          step: searchCount,
          searchRequest: '',
          contextsFound: allContexts.length,
          isFinal: true,
        });

        // Deduplicate contexts by file path
        const uniqueContexts = deduplicateContexts(allContexts);

        return {
          success: true,
          summary: result.text || 'Exploration completed but no summary was generated.',
          contexts: uniqueContexts,
          searchCount,
          durationMs: Date.now() - startTime,
        };
      } catch (error) {
        return {
          success: false,
          summary: '',
          contexts: deduplicateContexts(allContexts),
          searchCount,
          durationMs: Date.now() - startTime,
          error: error instanceof Error ? error.message : String(error),
        };
      }
    })();

    if (timeoutPromise) {
      try {
        return await Promise.race([runPromise, timeoutPromise]);
      } catch (error) {
        return {
          success: false,
          summary: '',
          contexts: deduplicateContexts(allContexts),
          searchCount,
          durationMs: Date.now() - startTime,
          error: error instanceof Error ? error.message : String(error),
        };
      }
    }

    return runPromise;
  }

  const exploreToolSchema = z.object({
    searchRequest: z
      .string()
      .describe('What to explore in the codebase. Be specific about what you want to understand.'),
  });

  /** The Vercel AI tool for use in parent agents */
  const exploreTool = tool({
    description:
      'Explore a codebase to answer questions about code structure, find implementations, trace call chains, and understand architecture. Returns a summary of findings. Use this for broad exploration rather than pinpoint keyword searches.',
    inputSchema: exploreToolSchema,
    execute: async (params: z.infer<typeof exploreToolSchema>) => {
      const result = await runExplore(params.searchRequest);
      return {
        success: result.success,
        summary: result.summary,
        searchCount: result.searchCount,
        error: result.error,
      };
    },
  });

  return {
    /** Vercel AI SDK tool for use as a tool in parent agents */
    tool: exploreTool,

    /** Run exploration with messaging support */
    run(prompt: string): ExploreSession {
      const messageHandlers: MessageHandler[] = [];
      const stepHandlers: StepHandler[] = [];

      // Start the run lazily (after handlers are attached)
      let resultPromise: Promise<ExploreResult> | null = null;

      const session: ExploreSession = {
        on(event: string, handler: any) {
          if (event === 'message') messageHandlers.push(handler);
          if (event === 'step') stepHandlers.push(handler);
          return session;
        },
        send(_text: string) {
          // Convenience method — pause-and-ask flow uses the reply callback
        },
        get result() {
          if (!resultPromise) {
            resultPromise = runExplore(prompt, {
              onStep: (step) => {
                for (const handler of stepHandlers) handler(step);
              },
              onMessage: (msg, reply) => {
                if (messageHandlers.length > 0) {
                  for (const handler of messageHandlers) handler(msg, reply);
                } else {
                  // No handlers, auto-reply
                  reply('');
                }
              },
            });
          }
          return resultPromise;
        },
      };

      return session;
    },

    /** Stream exploration events as an async generator */
    async *stream(prompt: string): AsyncGenerator<ExploreEvent, ExploreResult, undefined> {
      const events: ExploreEvent[] = [];
      let resolveNext: (() => void) | null = null;
      let done = false;

      const resultPromise = runExplore(prompt, {
        onStep: (step) => {
          events.push({
            type: 'step',
            step: step.step,
            searchRequest: step.searchRequest,
            contextsFound: step.contextsFound,
            isFinal: step.isFinal,
          });
          resolveNext?.();
        },
        onMessage: (msg, reply) => {
          events.push({
            type: 'message',
            from: msg.from,
            content: msg.content,
            timestamp: msg.timestamp,
          });
          resolveNext?.();
          // Auto-reply in streaming mode
          reply('Continue with your best judgment.');
        },
      });

      // Yield events as they come in
      while (!done) {
        if (events.length > 0) {
          yield events.shift()!;
        } else {
          // Check if the result is already done
          const raceResult = await Promise.race([
            resultPromise.then((r) => ({ type: 'done' as const, result: r })),
            new Promise<{ type: 'event' }>((resolve) => {
              resolveNext = () => resolve({ type: 'event' });
            }),
          ]);

          if (raceResult.type === 'done') {
            // Drain remaining events
            while (events.length > 0) {
              yield events.shift()!;
            }
            done = true;
            return raceResult.result;
          }
        }
      }

      return await resultPromise;
    },
  };
}

/** Deduplicate contexts, keeping the latest version of each file */
function deduplicateContexts(contexts: WarpGrepContext[]): WarpGrepContext[] {
  const map = new Map<string, WarpGrepContext>();
  for (const ctx of contexts) {
    const existing = map.get(ctx.file);
    if (!existing || ctx.content.length > existing.content.length) {
      map.set(ctx.file, ctx);
    }
  }
  return Array.from(map.values());
}
